import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChartLineUp,
  CodeSimple,
  ShieldCheck,
  Star,
  Briefcase,
  Quotes,
  HandHeart,
  Target,
  Lock,
  Compass,
  VideoCamera,
  Detective,
  CheckCircle,
  MagnifyingGlass,
  ChatCircleText,
  Trophy,
  Clock,
  PaperPlaneTilt,
} from "@phosphor-icons/react";

const CALENDLY_URL = "https://calendly.com/rh-mpadvisory/30min";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ==================== HERO ==================== */}
      <section className="bg-navy-deep text-white noise-overlay" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-14">

            {/* TEXT COLUMN — explicit max-width, no grid tricks */}
            <div className="w-full lg:flex-1 lg:max-w-2xl">
              <span className="inline-block px-4 py-1.5 rounded-full border border-white/15 text-purple-accent eyebrow backdrop-blur-sm">
                Cabinet de recrutement indépendant
              </span>

              <h1 className="mt-6 font-serif font-normal leading-[1.1] tracking-tight text-[clamp(2rem,4.5vw,3.25rem)]">
                Votre partenaire recrutement en{" "}
                <span className="font-serif-italic text-purple-accent">Sales</span>{" "}
                & <span className="font-serif-italic text-purple-accent">IT</span>
              </h1>

              <div className="accent-line mt-6" />

              <p className="mt-5 text-[15px] lg:text-base text-white/75 leading-relaxed font-light max-w-xl">
                MP Advisory accompagne les entreprises et les talents Sales et IT
                dans la réussite de leurs ambitions. Expertise métier, proximité
                et engagement pour des recrutements durables et performants.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
                <Link
                  to="/candidats"
                  data-testid="hero-cta-candidate"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-6 py-3 text-[14px] font-medium transition-all hover:-translate-y-0.5 shadow-purple-glow"
                >
                  Je suis candidat
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/entreprises"
                  data-testid="hero-cta-company"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/30 hover:border-white hover:bg-white/5 text-white px-6 py-3 text-[14px] font-medium transition-all"
                >
                  Je suis entreprise
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-meeting"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-purple-accent/40 hover:border-purple-accent text-white px-5 py-3 text-[13px] font-medium transition-all hover:-translate-y-0.5 backdrop-blur-sm"
                >
                  <VideoCamera size={16} weight="duotone" className="text-purple-accent" />
                  Prendre RDV en visio
                </a>
              </div>

              <p className="mt-4 text-[12px] text-white/55 font-serif italic">
                Échange confidentiel et sans engagement.
              </p>
            </div>

            {/* IMAGE COLUMN — fixed sizing, no aspect tricks */}
            <div className="hidden lg:block lg:w-[400px] xl:w-[440px] shrink-0">
              <div className="relative h-[500px] xl:h-[520px] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-floating">
                <img
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="Consultante MP Advisory — cabinet de recrutement Sales et IT"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: "50% 25%" }}
                />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy-deep/50 to-transparent pointer-events-none" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== STATS (séparée) ==================== */}
      <section className="bg-navy-light text-white py-10 lg:py-12 border-t border-white/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: HandHeart, value: "10 ans", label: "Expérience recrutement" },
              { icon: Briefcase, value: "120+", label: "Entreprises partenaires" },
              { icon: ShieldCheck, value: "98%", label: "Taux de satisfaction" },
              { icon: Star, value: "2", label: "Domaines d'expertise" },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 sm:gap-4" data-testid={`stat-${i}`}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-accent/15 flex items-center justify-center shrink-0">
                  <s.icon size={24} weight="duotone" className="text-purple-accent" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-serif text-white leading-tight">{s.value}</div>
                  <div className="text-[11px] sm:text-xs text-white/60 mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== À PROPOS ==================== */}
      <section className="bg-body-bg py-20 lg:py-28" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow text-purple-accent">À propos de MP Advisory</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-deep leading-[1.1]">
                Une approche humaine, ciblée et <span className="font-serif-italic text-purple-accent">exigeante</span> du recrutement
              </h2>
              <div className="accent-line mt-8" />
              <p className="mt-8 text-slate-600 leading-relaxed text-[16px]">
                MP Advisory accompagne les entreprises et les talents dans leurs
                projets de recrutement Sales et IT. L'approche repose sur une
                compréhension précise des besoins, une sélection rigoureuse des
                profils et un accompagnement personnalisé à chaque étape.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Clock, title: "10 ans d'expérience", desc: "Une expertise terrain construite au contact des décideurs et des talents Sales et IT." },
                { icon: Target, title: "Expertise Sales & IT", desc: "Une connaissance fine des métiers, des compétences et des écosystèmes." },
                { icon: HandHeart, title: "Candidats & entreprises", desc: "Un accompagnement dédié pour les deux côtés de la relation, sans compromis." },
                { icon: Lock, title: "Confidentialité absolue", desc: "Une démarche discrète et personnalisée, du brief jusqu'à la prise de poste." },
              ].map((b, i) => (
                <div
                  key={i}
                  data-testid={`about-card-${i}`}
                  className="bg-white rounded-2xl p-6 lg:p-7 border border-gray-100 shadow-subtle lift-on-hover"
                >
                  <div className="w-11 h-11 rounded-xl bg-purple-soft flex items-center justify-center">
                    <b.icon size={22} weight="duotone" className="text-purple-accent" />
                  </div>
                  <h3 className="mt-5 font-serif text-lg sm:text-xl text-navy-deep">{b.title}</h3>
                  <p className="mt-2 text-slate-600 text-[14px] leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== DOMAINES D'EXPERTISE ==================== */}
      <section className="bg-white py-24 lg:py-32" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="eyebrow text-purple-accent">Nos expertises</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-deep leading-[1.1]">
              Deux <span className="font-serif-italic text-purple-accent">domaines d'expertise</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              {
                icon: ChartLineUp,
                title: "Sales",
                desc: "Recrutement de commerciaux, Business Developers, Account Executives, SDR/BDR, Key Account Managers, Sales Managers, Customer Success Managers et profils terrain.",
                tags: ["Commerciaux", "Business Developers", "KAM", "Sales Managers", "CSM"],
              },
              {
                icon: CodeSimple,
                title: "IT",
                desc: "Recrutement de développeurs, ingénieurs, chefs de projet, Product Owners, Tech Leads, consultants, profils data, cybersécurité et experts digitaux.",
                tags: ["Développeurs", "Tech Leads", "Product Owners", "Data", "Cybersécurité"],
              },
            ].map((b, i) => (
              <article
                key={i}
                data-testid={`expertise-card-${i}`}
                className="bg-body-bg rounded-3xl p-8 lg:p-12 border border-gray-200 shadow-subtle lift-on-hover"
              >
                <div className="w-14 h-14 rounded-2xl bg-purple-accent/15 flex items-center justify-center">
                  <b.icon size={30} weight="duotone" className="text-purple-accent" />
                </div>
                <h3 className="mt-7 font-serif text-3xl text-navy-deep">{b.title}</h3>
                <p className="mt-4 text-slate-600 text-[15px] leading-relaxed">{b.desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {b.tags.map((t) => (
                    <span
                      key={t}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-purple-accent/20 text-navy-deep font-medium"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== POUR LES ENTREPRISES ==================== */}
      <section className="bg-body-bg py-24 lg:py-32" data-testid="for-companies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-purple-accent">Pour les entreprises</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-deep leading-[1.1]">
              Des recrutements ciblés pour vos <span className="font-serif-italic text-purple-accent">enjeux business</span>
            </h2>
            <p className="mt-6 text-slate-600 text-[16px] leading-relaxed">
              MP Advisory aide les entreprises à identifier, évaluer et attirer les bons profils
              Sales et IT, en tenant compte du poste, de l'environnement, de la culture d'entreprise
              et des objectifs de développement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Detective, num: "01", title: "Analyse du besoin", desc: "Compréhension du poste, des enjeux, du contexte et des critères de réussite." },
              { icon: MagnifyingGlass, num: "02", title: "Sourcing qualifié", desc: "Identification de profils pertinents, actifs ou en veille, dans les métiers Sales et IT." },
              { icon: CheckCircle, num: "03", title: "Évaluation structurée", desc: "Analyse des compétences, du parcours, des motivations et de l'adéquation avec l'entreprise." },
              { icon: HandHeart, num: "04", title: "Suivi jusqu'à l'intégration", desc: "Accompagnement du processus jusqu'à la prise de poste du candidat." },
            ].map((c, i) => (
              <div
                key={i}
                data-testid={`for-companies-card-${i}`}
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-subtle lift-on-hover relative"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl bg-purple-soft flex items-center justify-center">
                    <c.icon size={24} weight="duotone" className="text-purple-accent" />
                  </div>
                  <span className="step-number text-3xl text-purple-accent/30">{c.num}</span>
                </div>
                <h3 className="mt-6 font-serif text-xl text-navy-deep">{c.title}</h3>
                <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/formulaire"
              data-testid="for-companies-cta"
              className="inline-flex items-center gap-2 rounded-full bg-navy-deep hover:bg-navy-light text-white px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5"
            >
              Confier un recrutement
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== POUR LES CANDIDATS ==================== */}
      <section className="bg-white py-24 lg:py-32" data-testid="for-candidates-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-purple-accent">Pour les candidats</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-deep leading-[1.1]">
              Un accompagnement <span className="font-serif-italic text-purple-accent">confidentiel</span> pour faire évoluer votre carrière
            </h2>
            <p className="mt-6 text-slate-600 text-[16px] leading-relaxed">
              Que vous soyez en recherche active ou simplement à l'écoute du marché, MP Advisory
              vous accompagne avec discrétion pour identifier les opportunités Sales et IT alignées
              avec votre parcours, vos attentes et vos ambitions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Target, title: "Opportunités ciblées", desc: "Des postes sélectionnés selon votre profil et votre projet professionnel." },
              { icon: ChatCircleText, title: "Échange personnalisé", desc: "Un premier échange pour comprendre votre parcours, vos attentes et vos priorités." },
              { icon: Trophy, title: "Conseils carrière", desc: "Accompagnement sur votre positionnement, votre CV, vos entretiens et votre rémunération." },
              { icon: Lock, title: "Confidentialité garantie", desc: "Votre démarche reste confidentielle à chaque étape." },
            ].map((c, i) => (
              <div
                key={i}
                data-testid={`for-candidates-card-${i}`}
                className="bg-body-bg rounded-2xl p-7 border border-gray-200 shadow-subtle lift-on-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-accent/15 flex items-center justify-center">
                  <c.icon size={24} weight="duotone" className="text-purple-accent" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-navy-deep">{c.title}</h3>
                <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/formulaire"
              data-testid="for-candidates-cta"
              className="inline-flex items-center gap-2 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-purple-glow"
            >
              Déposer ma candidature
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== POURQUOI CHOISIR ==================== */}
      <section className="bg-navy-deep text-white py-24 lg:py-32 relative overflow-hidden noise-overlay" data-testid="why-section">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-accent/10 rounded-full blur-[140px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="eyebrow text-purple-accent">Pourquoi MP Advisory</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
              Pourquoi <span className="font-serif-italic text-purple-accent">choisir MP Advisory</span> ?
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: HandHeart, title: "Approche humaine et directe" },
              { icon: CheckCircle, title: "Sélection rigoureuse des profils" },
              { icon: Target, title: "Connaissance des métiers Sales et IT" },
              { icon: Lock, title: "Accompagnement réactif et confidentiel" },
              { icon: Compass, title: "Relation durable, candidats et entreprises" },
            ].map((c, i) => (
              <div
                key={i}
                data-testid={`why-card-${i}`}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 backdrop-blur-sm hover:bg-white/[0.08] transition-colors text-center flex flex-col items-center"
              >
                <div className="w-14 h-14 rounded-xl bg-purple-accent/15 flex items-center justify-center">
                  <c.icon size={26} weight="duotone" className="text-purple-accent" />
                </div>
                <h3 className="mt-5 font-serif text-base lg:text-lg leading-snug">{c.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TÉMOIGNAGES (placeholders) ==================== */}
      <section className="bg-white py-24 lg:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="eyebrow text-purple-accent">Témoignages</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-deep leading-[1.1]">
              Ils nous ont fait <span className="font-serif-italic text-purple-accent">confiance</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <article
                key={i}
                data-testid={`testimonial-placeholder-${i}`}
                className="bg-body-bg rounded-2xl p-8 border border-dashed border-gray-300 flex flex-col items-start gap-5 min-h-[220px]"
              >
                <Quotes size={28} weight="duotone" className="text-purple-accent/40" />
                <p className="font-serif italic text-lg text-slate-400 leading-relaxed">
                  Témoignage client à venir.
                </p>
                <div className="mt-auto pt-4 border-t border-gray-200 w-full">
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">
                    Nom · Fonction
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRENDRE RDV ==================== */}
      <section className="bg-body-bg py-24 lg:py-32" data-testid="booking-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="eyebrow text-purple-accent">Prendre rendez-vous</p>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-6xl text-navy-deep leading-[1.1]">
            Échangeons sur <span className="font-serif-italic text-purple-accent">votre projet.</span>
          </h2>
          <p className="mt-6 lg:mt-8 text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Vous êtes une entreprise avec un besoin de recrutement ou un candidat à l'écoute de
            nouvelles opportunités Sales ou IT ? Planifiez un échange de 30 minutes avec MP Advisory.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3 sm:gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-cta-meeting"
              className="group inline-flex items-center gap-2 rounded-full bg-purple-accent hover:bg-purple-accent-hover text-white px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-purple-glow"
            >
              <VideoCamera size={18} weight="duotone" />
              Prendre RDV en visio 30 min
              <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/formulaire"
              data-testid="booking-cta-message"
              className="inline-flex items-center gap-2 rounded-full border border-navy-deep/20 hover:border-navy-deep text-navy-deep px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5"
            >
              <PaperPlaneTilt size={18} weight="duotone" />
              Envoyer un message
            </Link>
          </div>
          <p className="mt-5 text-[13px] text-slate-500 font-serif italic">
            Échange confidentiel et sans engagement.
          </p>
        </div>
      </section>
    </div>
  );
}
